// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Group, Skeleton, Space, Stack, Text } from '@mantine/core';
import { useContext, useState } from 'react';
import { useElementSize } from '@mantine/hooks';
import { useDrop } from 'react-dnd-latest';
import { DocumentContext } from '../../Service/Context/DocumentContextV2';
import { ProfileContext } from '../../Service/Context/ProfileContext';
import BookitupSegmentedControl from '../../Atoms/BookitupSegmentedControl';
import { DataS } from '../../Service/DataS';
import { DragItem, PositionDragItem, TaxType } from '../../Service/DocumentT';
import PositionHeader from '../../Molecules/Document/Position/PositionHeader';
import DocumentPosition from '../../Molecules/Document/Position/DocumentPositionV2';
import DropArea from '../../Atoms/DropArea';
import { EventRichInput, RichInput } from '../tiptap/TiptapEditorInput';
import DocumentSum from '../../Molecules/Document/DocumentSum';
import DocumentPartialInvoices from '../../Molecules/Event/Documents/invoices/DocumentPartialInvoices';
import UnassginedPartialInvoices from '../../Molecules/Event/Documents/invoices/UnassignedPartialInvoices';
import PositionGroups from '../../Molecules/Document/Position/PositionGroups';
import SkeletonItems, { EmptyPositionsSkeleton } from '../../Atoms/SkeletonItems';
import { openAddProductModal } from '../../Molecules/Document/Position/AddProductModal';
import { useEventScope } from '../../Service/hooks/useEventScope';
import DocumentBubbleMenu from '../tiptap/toolbars/BubbleMenu';
import { TiptapS } from '../../Service/TiptapS';
import { DocumentS } from '../../Templates/Documents/DocumentS';
import { ThemeText } from '../../Atoms/ThemeText';
import { usePositionProducts } from '../../Service/hooks/usePositionProducts';
import { ViolationContext } from '../../Service/Context/ViolationContext';
import { LicenceS } from '../../Service/LicenceS';
import LinkText from '../../Atoms/LinkText';
import { WindowContext } from '../../Service/Context/WindowContext';

const DocumentSkeleton = () => {
  const { ref, width } = useElementSize();
  const { isEventScope } = useEventScope();
  const { loading, bookitupDocument, positionHandlers, savePatch, groupHandlers, refresh } =
    useContext(DocumentContext);
  const { executeWithLicenceCheck } = useContext(ViolationContext);
  const [addingPosition, setAddingPosition] = useState(false);
  const { products } = usePositionProducts();
  const { addPosition, removePositionFromGroup, addProducts } = positionHandlers;
  const { addGroup } = groupHandlers;
  const { profile } = useContext(ProfileContext);
  const { sm: isMobile } = useContext(WindowContext);
  const { smallScaleBusiness } = profile ?? {};

  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: DragItem.POSITION,
      canDrop: (item: PositionDragItem) => item.position.groupId !== null,
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: !!monitor.canDrop(),
      }),
      drop: (item: PositionDragItem) => removePositionFromGroup(item.position.id),
    }),
    [],
  );

  const addProduct = () =>
    executeWithLicenceCheck(LicenceS.Restrictions.PRODUCT_USAGE, () => {
      openAddProductModal({ products, onFinish: (p) => addProducts(p) });
    });

  const addPositionHandler = async () => {
    setAddingPosition(true);
    await addPosition();
    setAddingPosition(false);
  };

  const { draft, taxType, documentType, positions, currencySymbol } = bookitupDocument ?? {
    positions: [],
    currencySymbol: '€',
  };

  // const grouplessPositions = positions.filter((position) => position.groupId === null);
  const emptyGroups = true; // positionGroups.length === 0;
  const emptyPositions = positions.length === 0; // grouplessPositions.length === 0;
  const isPartialInvoice = documentType === 'PARTIAL_INVOICE';
  const usePreviewVersion = width < 800;

  return (
    <div ref={ref}>
      <Group position="apart" mb="md">
        <ThemeText size="lg" weight="bolder">
          {loading && 'Laden ...'}
          {!loading && bookitupDocument && (
            <ThemeText>
              {DocumentS.resolveLabel(bookitupDocument)}
              {draft && (
                <Text span ml={5} color="dimmed" size="sm" weight="normal">
                  (Entwurf)
                </Text>
              )}
            </ThemeText>
          )}
        </ThemeText>
        {!smallScaleBusiness && (
          <BookitupSegmentedControl
            value={taxType}
            color="blue"
            data={DataS.netGrossOptions}
            radius="md"
            onChange={(val: TaxType) => savePatch({ taxType: val })}
            sx={{ minWidth: 160 }}
          />
        )}
      </Group>
      {isEventScope && bookitupDocument && (
        <EventRichInput
          label="Kopftext"
          value={bookitupDocument.headRow}
          patchField={(val: string) => savePatch({ headRow: val })}
          menu={<DocumentBubbleMenu />}
          styles={TiptapS.TextInputStyles}
        />
      )}
      {!isEventScope && bookitupDocument && (
        <RichInput
          label="Kopftext"
          value={bookitupDocument.headRow}
          patchField={(val: string) => savePatch({ headRow: val })}
          menu={<DocumentBubbleMenu />}
          styles={TiptapS.TextInputStyles}
        />
      )}

      <Space h="xs" />
      <PositionHeader
        documentVersion
        desktopVersion={!usePreviewVersion}
        showDiscount={!isPartialInvoice}
        currencySymbol={currencySymbol}
        isPartialInvouce={documentType === 'PARTIAL_INVOICE'}
        language={bookitupDocument?.language}
        sticky={!isMobile}
      />
      <Stack mt={5}>
        {/* <PositionGroups usePreviewVersion={usePreviewVersion} /> */}
        {loading && <SkeletonItems height={100} />}
        {!loading && emptyPositions && !addingPosition && emptyGroups && <EmptyPositionsSkeleton />}
        {bookitupDocument &&
          positions.map((position) => (
            <DocumentPosition
              key={position.id}
              previewVersion={usePreviewVersion}
              bookitupDocument={bookitupDocument}
              position={position}
              handlers={positionHandlers}
              products={products}
              refreshDocument={refresh}
            />
          ))}
      </Stack>
      {/* <div ref={drop}>{canDrop && <DropArea isOver={isOver} />}</div> */}
      {addingPosition && <Skeleton mt="sm" width="100%" height={100} />}
      {!canDrop && !addingPosition && (
        <Group mt="md">
          {!isPartialInvoice && emptyPositions && !addingPosition && !loading && (
            <>
              <LinkText onClick={addPositionHandler}>+ Position hinzufügen</LinkText>
              <LinkText onClick={addProduct}>+ Produkt hinzufügen</LinkText>
            </>
          )}
          {/* {emptyGroups && (
            <Text color="blue" variant="link" sx={{ cursor: 'pointer' }} onClick={() => addGroup()}>
              + Gruppe hinzufügen
            </Text>
          )} */}
        </Group>
      )}
      <Space h="md" />
      {bookitupDocument && <DocumentSum baseDocument={bookitupDocument} savePatch={savePatch} />}
      <DocumentPartialInvoices />
      {documentType === 'INVOICE' && <UnassginedPartialInvoices />}
      <Space h="xl" />
      {isEventScope && bookitupDocument && (
        <EventRichInput
          label="Fußtext"
          value={bookitupDocument.endRow}
          patchField={(val: string) => savePatch({ endRow: val })}
          menu={<DocumentBubbleMenu />}
          styles={{ minHeight: 150 }}
        />
      )}
      {!isEventScope && bookitupDocument && (
        <RichInput
          label="Fußtext"
          value={bookitupDocument.endRow}
          patchField={(val: string) => savePatch({ endRow: val })}
          menu={<DocumentBubbleMenu />}
          styles={{ minHeight: 150 }}
        />
      )}
    </div>
  );
};

export default DocumentSkeleton;
